import * as Sentry from '@sentry/react'

import { HelpCircle, Key, MessageSquare, Mic, Smartphone } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import apps, { App } from '@/apps'
import AppIcon from '@/components/app-icon'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import useActionTimer from '@/hooks/useActionTimer'
import { useAPI } from '@/hooks/useAPI'
import { cn } from '@/lib/utils'
import { actions } from '@/slices/auth'
import { useAppDispatch } from '@/store'
import { accountQuestionnaire } from '@/thunks/accounts/questionnaire'
import { startFreeTrial } from '@/thunks/billing/free-trial'

import { zodResolver } from '@hookform/resolvers/zod'

const schema = z.object({
  companyName: z.string().min(1),
  annualCallVolume: z.string().min(1),
  authenticationMethod: z.string().min(1),
  apps: z.array(z.enum<App, [App, ...App[]]>(apps.map(app => app.id) as [App, ...App[]])),
})

type FormData = z.infer<typeof schema>

const callVolumeOptions = [
  { value: 'under-10k', label: 'Under 10,000' },
  { value: '10k-50k', label: '10,000 - 50,000' },
  { value: '50k-100k', label: '50,000 - 100,000' },
  { value: '100k-500k', label: '100,000 - 500,000' },
  { value: '500k-1m', label: '500,000 - 1 million' },
  { value: 'over-1m', label: 'Over 1 million' },
]

const authMethods = [
  { value: 'kba', label: 'Knowledge-Based Authentication (KBA)', icon: MessageSquare, color: 'blue' },
  { value: 'otp', label: 'One-Time Passcode (OTP)', icon: Key, color: 'purple' },
  { value: 'voice-bio', label: 'Voice Biometrics', icon: Mic, color: 'green' },
  { value: 'passkey', label: 'Passkey', icon: Smartphone, color: 'orange' },
  { value: 'other', label: 'Other', icon: HelpCircle, color: 'gray' },
]

export default function OnboardingDialog() {
  const [isOpen, setIsOpen] = useState(true)

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: '',
      annualCallVolume: '',
      authenticationMethod: '',
      apps: [],
    },
  })

  const timer = useActionTimer()
  const [submit] = useAPI(accountQuestionnaire)
  const [start] = useAPI(startFreeTrial)
  const dispatch = useAppDispatch()
  const handleSubmit = useCallback(
    async (data: FormData) => {
      try {
        timer.start()

        const p = submit({
          currentAuthenticationMethod: data.authenticationMethod,
          companyName: data.companyName,
          annualCallVolume: data.annualCallVolume,
          apps: data.apps as App[],
        })

        // Leave time for the UI to update from Get Started -> Launch App
        data.apps.forEach(app => {
          start({ app: app as App })
        })
        const p2 = new Promise(resolve => setTimeout(resolve, 1_500))

        await Promise.all([p, p2])
      } catch (error) {
        console.log('Questionnaire error', error, data)
        Sentry.captureException(error)
        timer.failed()
      } finally {
        setIsOpen(false)
        dispatch(actions.setHasOnboarded(true))
      }
    },
    [submit, start, dispatch, timer]
  )

  const selectedApps = form.watch('apps')

  const handleToggleApp = useCallback(
    (app: App) => {
      const currentApps = form.getValues('apps')
      const newApps = currentApps.includes(app) ? currentApps.filter(id => id !== app) : [...currentApps, app]
      form.setValue('apps', newApps)
    },
    [form]
  )

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden"
        onPointerDownOutside={e => e.preventDefault()}
      >
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogHeader>
            <DialogTitle>Welcome to Journey!</DialogTitle>
            <DialogDescription>
              Help us personalize your experience by answering a few quick questions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input id="company-name" placeholder="Enter your company name" {...form.register('companyName')} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="call-volume">Annual Call Volume</Label>
              <Controller
                control={form.control}
                name="annualCallVolume"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="call-volume">
                      <SelectValue placeholder="Select annual call volume" />
                    </SelectTrigger>
                    <SelectContent>
                      {callVolumeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="auth-method">Current Authentication Method</Label>
              <Controller
                control={form.control}
                name="authenticationMethod"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="auth-method">
                      <SelectValue placeholder="Select authentication method" />
                    </SelectTrigger>
                    <SelectContent>
                      {authMethods.map(method => (
                        <SelectItem key={method.value} value={method.value}>
                          <div className="flex gap-3 items-center">
                            <div
                              className={cn(
                                'flex items-center justify-center w-6 h-6 rounded',
                                `bg-${method.color}-50`
                              )}
                            >
                              <method.icon className={cn('w-4 h-4', `text-${method.color}-500`)} />
                            </div>
                            <span>{method.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Select Apps You&apos;re Interested In</Label>
              <div className="grid grid-cols-3 gap-3">
                {apps
                  .filter(app => app.isReleased)
                  .map(app => (
                    <label
                      key={app.id}
                      htmlFor={`app-checkbox-${app.id}`}
                      className={cn(
                        'relative p-3 rounded-lg border cursor-pointer transition-all block',
                        selectedApps.includes(app.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-muted hover:border-muted-foreground/30'
                      )}
                    >
                      <Checkbox
                        id={`app-checkbox-${app.id}`}
                        checked={selectedApps.includes(app.id)}
                        onCheckedChange={() => handleToggleApp(app.id)}
                        className="absolute top-2 right-2"
                      />
                      <div className="flex gap-4 items-center">
                        <AppIcon Icon={app.icon} color={app.color} size="md" />
                        <span className="text-sm font-medium">{app.title}</span>
                      </div>
                    </label>
                  ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-row items-end">
            {/* <Button variant="ghost" onClick={handleSkip}>
            Skip
          </Button> */}
            <Button type="submit" disabled={!form.formState.isValid || timer.isLoading}>
              Complete Setup
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
